import { authFetch } from "@/lib/authFetch";
import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Flame, Check, Settings, RefreshCw } from "lucide-react";

import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useAuth } from "@/hooks/useAuth";
import { cn } from "@/lib/utils";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Sessão UI-1 — DailyHabitCard
//
// Pega o primeiro hábito não-checado do dia (active + frequency=daily +
// !checked_today). O schema atual de /api/habits é BOOLEANO (sem unit /
// target / default_increments) — quick actions grid quantitativo do
// brief não se aplica. Pivot honesto: 1 CTA grande "Marcar concluído"
// pra MVP. Deixa quantitativo pra quando o backend expor habit.config.
//
// Empty states:
//   - sem hábitos cadastrados → CTA navegação "Configurar primeiro hábito"
//   - todos checados hoje     → "Hábitos do dia concluídos. Excelente."

export default function DailyHabitCard({ onNavigate, onAfterCheckin }) {
  const { user } = useAuth();
  const userId = user?.id;
  const [habits, setHabits] = useState([]);
  const [loading, setLoading] = useState(true);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const url = userId
        ? `${API_URL}/api/habits?user_id=${encodeURIComponent(userId)}`
        : `${API_URL}/api/habits`;
      const r = await authFetch(url);
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const data = await r.json();
      const list = Array.isArray(data?.habits) ? data.habits : [];
      // Filtra: ativos + frequência diária + do user atual (se userId disponível)
      const filtered = list.filter(h =>
        h.is_active !== false &&
        (h.frequency === "daily" || !h.frequency) &&
        (!userId || !h.userId || h.userId === userId),
      );
      setHabits(filtered);
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setLoading(false);
    }
  }, [userId]);

  useEffect(() => { load(); }, [load]);

  const target = habits.find(h => !h.checked_today) || null;
  const allDone = habits.length > 0 && !target;

  const handleCheckin = async () => {
    if (!target || posting) return;
    setPosting(true);
    try {
      const r = await authFetch(`${API_URL}/api/habits/${target.id}/checkin`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
      });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await load();
      onAfterCheckin?.();
    } catch (e) {
      setError(String(e.message || e));
    } finally {
      setPosting(false);
    }
  };

  const Header = ({ children }) => (
    <div className="flex items-center justify-between mb-5">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-lg bg-status-warning/10 flex items-center justify-center">
          <Flame className="w-4 h-4 text-status-warning" aria-hidden="true" />
        </div>
        <span
          className="text-caption font-semibold uppercase text-status-warning/80"
          style={{ letterSpacing: "0.12em" }}
        >
          Hábito do dia
        </span>
      </div>
      {children}
    </div>
  );

  if (loading) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[260px]" data-testid="daily-habit-card">
        <Header />
        <div className="flex items-center gap-2 text-foreground-muted">
          <RefreshCw className="w-4 h-4 animate-spin" />
          <span className="text-caption">Carregando hábito…</span>
        </div>
      </Card>
    );
  }

  if (habits.length === 0) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[260px] flex flex-col" data-testid="daily-habit-card">
        <Header />
        <div className="flex-1 flex flex-col items-start justify-center gap-3">
          <div className="w-12 h-12 rounded-full bg-status-warning/10 flex items-center justify-center">
            <Flame className="w-5 h-5 text-status-warning" aria-hidden="true" />
          </div>
          <h3 className="font-display font-semibold text-foreground text-h3">
            Nenhum hábito ainda
          </h3>
          <p className="text-caption text-foreground-muted">
            Configure seu primeiro hábito pra começar uma sequência.
          </p>
          <Button variant="secondary" size="md" onClick={() => onNavigate?.("habits")} className="mt-1" data-testid="daily-habit-empty-cta">
            <Settings className="w-4 h-4" />
            Configurar primeiro hábito
          </Button>
        </div>
      </Card>
    );
  }

  if (allDone) {
    return (
      <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[260px] flex flex-col" data-testid="daily-habit-card">
        <Header />
        <div className="flex-1 flex flex-col items-start justify-center gap-3">
          <div className="w-12 h-12 rounded-full bg-brand-green/10 flex items-center justify-center">
            <Check className="w-5 h-5 text-brand-green" aria-hidden="true" />
          </div>
          <h3 className="font-display font-semibold text-foreground text-h3">
            Hábitos do dia concluídos
          </h3>
          <p className="text-caption text-foreground-muted">
            Excelente. Volte amanhã pra manter a sequência.
          </p>
          <Button variant="ghost" size="md" onClick={() => onNavigate?.("habits")} className="mt-1">
            Ver todos os hábitos
          </Button>
        </div>
      </Card>
    );
  }

  const streak = Number(target?.streak_current || 0);

  return (
    <Card variant="elevated" className="glass-card glow-hover p-6 md:p-7 min-h-[260px] flex flex-col" data-testid="daily-habit-card">
      <Header>
        {streak > 0 && (
          <Badge variant="warning" data-testid="daily-habit-streak">
            <Flame className="w-3 h-3 mr-1" />
            {streak} dia{streak === 1 ? "" : "s"}
          </Badge>
        )}
      </Header>

      <AnimatePresence mode="wait">
        <motion.div
          key={target?.id}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -6 }}
          transition={{ duration: 0.2 }}
          className="flex-1 flex flex-col"
        >
          <h3
            className="font-display font-bold text-foreground text-[40px] md:text-[48px] leading-[1.05] tracking-[-0.02em] mb-2"
            data-testid="daily-habit-name"
          >
            {target?.name || "Hábito"}
          </h3>
          {target?.description && (
            <p className="text-body text-foreground-muted mb-6 max-w-md">
              {target.description}
            </p>
          )}

          {error && (
            <p className="text-caption text-status-error mb-3">{error}</p>
          )}

          <div className="mt-auto">
            <Button
              variant="primary"
              size="lg"
              onClick={handleCheckin}
              disabled={posting}
              className={cn(
                "w-full md:w-auto bg-gradient-to-r from-status-warning to-brand-green",
                "text-surface-canvas font-semibold",
              )}
              data-testid="daily-habit-checkin"
              aria-label="Marcar hábito como concluído hoje"
            >
              <Check className="w-4 h-4" />
              {posting ? "Registrando…" : "Marcar concluído"}
            </Button>
          </div>
        </motion.div>
      </AnimatePresence>
    </Card>
  );
}
